// react
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

// radix
import * as RadixAvatar from '@radix-ui/react-avatar';

// components
import { Icon, Icons } from '@/components/atoms/Icon';

type AvatarSize = 'xxxs' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
const sizeMap = {
  xxxs: 16,
  xxs: 24,
  xs: 40,
  sm: 48,
  md: 64,
  lg: 112,
  xl: 192,
};

interface AvatarProps {
  className?: string;
  size?: AvatarSize;
  src?: string;
  fallback: string;
  icon?: Icons;
  onClick?: () => void;
}

/**
 * Avatar shows an image of the user.
 */
export const Avatar = ({
  className,
  size = 'sm',
  src,
  fallback,
  icon,
  onClick,
}: AvatarProps) => {
  // memos
  const iconSize = useMemo(() => {
    switch (size) {
      case 'xxxs':
        return 10;
      case 'xxs':
        return 12;
      case 'xs':
        return 16;
      case 'sm':
        return 20;
      case 'md':
        return 24;
      case 'lg':
        return 32;
      case 'xl':
        return 48;
      default:
        return 24;
    }
  }, [size]);

  const computedSrc = useMemo(() => {
    if (src?.includes('d=blank')) return undefined;
    return src;
  }, [src]);

  return (
    <AvatarRoot
      className={className}
      size={size}
      onClick={onClick}
      $isSrc={!!computedSrc}
    >
      {icon ? (
        <Icon icon={icon} fontSize={iconSize} />
      ) : (
        <>
          <AvatarImage src={computedSrc} alt='avatar' />
          <AvatarFallback size={size} delayMs={0}>
            {fallback}
          </AvatarFallback>
        </>
      )}
    </AvatarRoot>
  );
};

interface StyledAvatarProps {
  size?: AvatarSize;
  $isSrc?: boolean;
}
const AvatarRoot = styled(RadixAvatar.Root)<StyledAvatarProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  overflow: hidden;
  user-select: none;
  border-radius: 9999px;
  ${(props) =>
    !props.$isSrc &&
    css`
      background: ${props.theme.color.surfaces.lightPlum};
    `};
  color: ${(props) => props.theme.color.accent.moccasin};
  ${(props) => {
    let size = sizeMap[props.size || 'md'];
    return css`
      width: ${size}px;
      height: ${size}px;
      min-width: ${size}px;
      min-height: ${size}px;
      max-width: ${size}px;
      max-height: ${size}px;
    `;
  }}
`;

const AvatarImage = styled(RadixAvatar.Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
`;

interface AvatarFallbackProps {
  size?: AvatarSize;
}

const AvatarFallback = styled(RadixAvatar.Fallback)<AvatarFallbackProps>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => {
    switch (props.size) {
      case 'xxxs':
        return css`
          font: ${props.theme.typography.body.xs.regular};
        `;
      case 'xxs':
        return css`
          font: ${props.theme.typography.body.xs.regular};
        `;
      case 'xs':
        return css`
          font: ${props.theme.typography.body.md.regular};
        `;
      case 'sm':
        return css`
          font: ${props.theme.typography.body.lg.regular};
        `;
      case 'md':
        return css`
          font: ${props.theme.typography.body.xl.regular};
        `;
      case 'lg':
        return css`
          font: ${props.theme.typography.body.xxl.regular};
        `;
      case 'xl':
        return css`
          font: ${props.theme.typography.body.xxxl.regular};
        `;
    }
  }}
`;

Avatar.displayName = 'Avatar';

export default Avatar;
